import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, Building2, Trash2, Mail, CheckCircle, Clock, X } from 'lucide-react';

const UsersPage = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, tenantsRes] = await Promise.all([
        fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/auth/users`, { headers }),
        fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/tenants`, { headers }),
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
        setInvitations(data.invitations || []);
      }
      if (tenantsRes.ok) {
        setTenants(await tenantsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return { backgroundColor: '#fef3c7', color: '#d97706' };
      case 'AGENCY_USER':
        return { backgroundColor: '#dbeafe', color: '#2563eb' };
      case 'CLIENT_ADMIN':
        return { backgroundColor: '#d1fae5', color: '#059669' };
      case 'CLIENT_USER':
        return { backgroundColor: '#e5e7eb', color: '#374151' };
      default:
        return { backgroundColor: '#e5e7eb', color: '#374151' };
    }
  };

  const formatRole = (role) => role?.replace('_', ' ') || '';

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <Users size={24} />
            <h2 style={styles.title}>User Management</h2>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.actions}>
          <button style={styles.inviteBtn} onClick={() => setShowInviteModal(true)}>
            <UserPlus size={18} />
            Invite User
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : (
          <>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Active Users ({users.length})</h3>
              <div style={styles.userList}>
                {users.map((user) => (
                  <div key={user.id} style={styles.userCard}>
                    <div style={styles.userAvatar}>
                      {user.avatar ? (
                        <img src={user.avatar} alt="" style={styles.avatarImg} />
                      ) : (
                        <span>{user.name?.charAt(0) || user.email?.charAt(0)}</span>
                      )}
                    </div>
                    <div style={styles.userInfo}>
                      <span style={styles.userName}>{user.name || 'No name'}</span>
                      <span style={styles.userEmail}>{user.email}</span>
                    </div>
                    <span style={{ ...styles.roleBadge, ...getRoleBadgeStyle(user.role) }}>
                      {formatRole(user.role)}
                    </span>
                    {user.tenant && (
                      <span style={styles.tenantBadge}>
                        <Building2 size={12} />
                        {user.tenant.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {invitations.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Pending Invitations ({invitations.length})</h3>
                <div style={styles.userList}>
                  {invitations.map((inv) => (
                    <div key={inv.id} style={styles.userCard}>
                      <div style={{ ...styles.userAvatar, backgroundColor: '#fef3c7' }}>
                        <Mail size={18} color="#d97706" />
                      </div>
                      <div style={styles.userInfo}>
                        <span style={styles.userName}>{inv.email}</span>
                        <span style={styles.userEmail}>
                          <Clock size={12} /> Invited {new Date(inv.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <span style={{ ...styles.roleBadge, ...getRoleBadgeStyle(inv.role) }}>
                        {formatRole(inv.role)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {showInviteModal && (
          <InviteModal
            tenants={tenants}
            onClose={() => setShowInviteModal(false)}
            onSuccess={() => {
              setShowInviteModal(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

const InviteModal = ({ tenants, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('CLIENT_ADMIN');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteLink, setInviteLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/auth/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          role,
          hasFullAccess: role === 'AGENCY_USER' ? hasFullAccess : false,
          tenantIds: selectedTenants,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send invitation');
      }

      setInviteLink(data.inviteLink);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
  };

  if (inviteLink) {
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.inviteModal}>
          <div style={styles.successIcon}>
            <CheckCircle size={48} color="#22c55e" />
          </div>
          <h3 style={styles.inviteTitle}>Invitation Created!</h3>
          <p style={styles.inviteText}>Share this link with {email}:</p>
          <div style={styles.linkBox}>
            <input type="text" value={inviteLink} readOnly style={styles.linkInput} />
            <button onClick={copyLink} style={styles.copyBtn}>Copy</button>
          </div>
          <button onClick={onSuccess} style={styles.doneBtn}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.inviteModal}>
        <h3 style={styles.inviteTitle}>Invite User</h3>
        
        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={styles.select}
            >
              <option value="AGENCY_USER">Agency User (Your Team)</option>
              <option value="CLIENT_ADMIN">Client Admin</option>
              <option value="CLIENT_USER">Client User</option>
            </select>
          </div>

          {role === 'AGENCY_USER' && (
            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={hasFullAccess}
                  onChange={(e) => setHasFullAccess(e.target.checked)}
                />
                Full access to all tenants
              </label>
            </div>
          )}

          {(role !== 'AGENCY_USER' || !hasFullAccess) && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {role === 'AGENCY_USER' ? 'Accessible Tenants' : 'Tenant'}
              </label>
              <div style={styles.tenantList}>
                {tenants.map((tenant) => (
                  <label key={tenant.id} style={styles.tenantOption}>
                    <input
                      type={role === 'AGENCY_USER' ? 'checkbox' : 'radio'}
                      name="tenant"
                      checked={selectedTenants.includes(tenant.id)}
                      onChange={(e) => {
                        if (role === 'AGENCY_USER') {
                          setSelectedTenants(
                            e.target.checked
                              ? [...selectedTenants, tenant.id]
                              : selectedTenants.filter((id) => id !== tenant.id)
                          );
                        } else {
                          setSelectedTenants([tenant.id]);
                        }
                      }}
                    />
                    {tenant.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={styles.formActions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancel
            </button>
            <button type="submit" style={styles.submitBtn} disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    backgroundColor: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeBtn: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#6b7280',
  },
  actions: {
    padding: '16px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  inviteBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  section: {
    padding: '20px 24px',
    overflowY: 'auto',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '12px',
  },
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  userCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  userInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111827',
  },
  userEmail: {
    fontSize: '12px',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  roleBadge: {
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  tenantBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#374151',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
  },
  inviteModal: {
    width: '90%',
    maxWidth: '480px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
  },
  inviteTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  error: {
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  tenantList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    maxHeight: '150px',
    overflowY: 'auto',
  },
  tenantOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelBtn: {
    padding: '10px 16px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  successIcon: {
    textAlign: 'center',
    marginBottom: '16px',
  },
  inviteText: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '12px',
  },
  linkBox: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
  },
  linkInput: {
    flex: 1,
    padding: '10px 12px',
    fontSize: '13px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
  },
  copyBtn: {
    padding: '10px 16px',
    backgroundColor: '#e5e7eb',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  doneBtn: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default UsersPage;


